import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import type { MouseEvent, ReactNode } from 'react';
import { TreeDndHandle } from '@/components/tree-dnd/tree-dnd-handle';
import { treeDndUiStore } from '@/components/tree-dnd/tree-dnd-ui-store';
import { cn } from '@/lib/utils';

export type TreeNodeType = 'group' | 'condition';

interface TreeDndBoxProps {
  nodeType: TreeNodeType;
  dragHandleProps?: {
    listeners?: SyntheticListenerMap;
    attributes?: DraggableAttributes;
  };
  showHandle?: boolean;
  headerExtra?: ReactNode;
  children?: ReactNode;
  className?: string;
  highlighted?: boolean;
  /** Opens the condition editor when the box is clicked (excluding buttons). */
  onActivate?: () => void;
}

function shouldIgnoreActivateClick(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('button') || target.closest('[data-tree-dnd-no-activate]')
  );
}

const typeStyles: Record<TreeNodeType, string> = {
  group: 'border-border bg-card',
  condition: 'border-muted-foreground/20 bg-muted/20',
};

export function TreeDndBox({
  nodeType,
  dragHandleProps,
  showHandle = true,
  headerExtra,
  children,
  className,
  highlighted = false,
  onActivate,
}: TreeDndBoxProps) {
  const hasBody = Boolean(children);
  const handle = showHandle ? (
    <TreeDndHandle {...dragHandleProps} disabled={!dragHandleProps} />
  ) : null;

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (
      !onActivate ||
      shouldIgnoreActivateClick(event.target) ||
      treeDndUiStore.getState().isConditionActivateSuppressed()
    ) {
      return;
    }
    onActivate();
  };

  return (
    <div
      onClick={onActivate ? handleClick : undefined}
      className={cn(
        'rounded-lg border transition-[box-shadow,border-color,background-color] duration-300',
        typeStyles[nodeType],
        onActivate &&
          !highlighted &&
          'cursor-pointer hover:border-muted-foreground/40 hover:bg-muted/35',
        highlighted &&
          'border-primary bg-primary/5 shadow-[0_0_0_2px] shadow-primary/25',
        className
      )}
    >
      <div
        className={cn(
          'flex items-center gap-1 px-1 py-0.5',
          hasBody && 'border-b'
        )}
      >
        {handle}
        {headerExtra}
      </div>
      {hasBody ? (
        <div className="flex flex-col p-2 pl-3">{children}</div>
      ) : null}
    </div>
  );
}
